import React, { FC, useMemo, useState } from 'react';
import * as yup from 'yup';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { DiscountModel, FormModel, FormPaymentModes } from '../../../../../models/form/form';
import { RootState } from '../../../../../store';
import ModalComponent from '../../../../../components/modal/ModalComponent';
import SingleMode from './SingleMode';
import SelectInput from '../../../../../components/library/inputs/select/SelectInput';
import FixedDatesMode from './FixedDatesMode';
import { FieldArray, Formik, FormikProps } from 'formik';
import { noop } from '../../../../../utilities/FunctionUtilities';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

const defaultDiscount: DiscountModel = {
  value: 0,
  type: 'amount',
};

const defaultModes: FormPaymentModes = {
  modes: [
    {
      id: uuidv4(),
      mode: 'single',
      discount: defaultDiscount,
    },
  ],
  applyTo: [],
};

const modeComponents = {
  single: SingleMode,
  fixed_dates: FixedDatesMode,
  //   fixed_recurrence: FixedRecurrenceMode,
  //   subscription: SubscriptionMode,
};

const initialModes = {
  single: { mode: 'single', discount: defaultDiscount },
  fixed_dates: { mode: 'fixed_dates', discount: defaultDiscount, installments: [] },
};

const PaymentModes: FC<any> = React.forwardRef<FormikProps<any>>((props, ref) => {
  const form = useSelector((state: RootState) => state.formAdmin.form) as FormModel;
  const [showAddMode, setShowAddMode] = useState<boolean>(false);
  const [selectedMode, setSelectedMode] = useState<string>('');
  const intl = useIntl();
  const validationSchema = useMemo(() => {
    return yup.object().shape({
      modes: yup.array().of(
        yup.lazy((item) => {
          const { mode } = item;

          let schema = yup.object().shape({
            discount: yup
              .object()
              .shape({
                type: yup.string(),
                value: yup
                  .number()
                  .required(intl.formatMessage({ id: 'paymentModes.errors.discountValueRequired' }))
                  .min(0, intl.formatMessage({ id: 'paymentModes.errors.discountMin' }))
                  .when('type', {
                    is: (type) => type === 'percent',
                    then: (schema) => schema.max(100, intl.formatMessage({ id: 'paymentModes.errors.discountMax' })),
                  }),
              })
              .nullable(),
          });

          if (mode === 'fixed_dates') {
            schema = schema.shape({
              installments: yup
                .array()
                .of(
                  yup.object().shape({
                    date: yup
                      .date()
                      .typeError(intl.formatMessage({ id: 'paymentModes.errors.invalidDate' }))
                      .nullable()
                      .required(intl.formatMessage({ id: 'paymentModes.errors.dateRequired' }))
                      .test(
                        'date-strict-format',
                        intl.formatMessage({ id: 'paymentModes.errors.invalidDate' }),
                        (date, context) => {
                          const { originalValue } = context as any;
                          return moment(originalValue, 'YYYY-MM-DD', true).isValid();
                        },
                      ),
                    percent: yup
                      .number()
                      .min(1, intl.formatMessage({ id: 'paymentModes.errors.installmentMin' }))
                      .max(100, intl.formatMessage({ id: 'paymentModes.errors.installmentMax' }))
                      .required(),
                  }),
                )
                .test(
                  'sum-of-percentage',
                  intl.formatMessage({ id: 'paymentModes.errors.installmentsSum' }),
                  (installments) => {
                    const sum =
                      installments?.reduce((acc, { percent }) => {
                        return acc + (percent || 0);
                      }, 0) || 0;
                    return sum === 100;
                  },
                ),
            });
          }

          return schema;
        }) as any,
      ),
    });
  }, []);

  return (
    <>
      <div className="step-admin">
        <Formik
          initialValues={{ modes: form.paymentModes?.modes || defaultModes.modes }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={noop}
          innerRef={ref}
        >
          {({ values }) => {
            const containsSingle = (values.modes || defaultModes.modes).find((mode) => mode.mode == 'single');
            let addPaymentModeOptions = [{ text: 'Payment plan with dates', value: 'fixed_dates' }];
            if (!containsSingle) {
              addPaymentModeOptions = [{ text: 'Pay in Full', value: 'single' }, ...addPaymentModeOptions];
            }

            return (
              <React.Fragment>
                <form>
                  <FieldArray
                    name="modes"
                    render={(arrayHelpers) => {
                      return (
                        <div>
                          <ModalComponent
                            show={showAddMode}
                            modalHeading={'Add payment mode'}
                            closeSettings={() => setShowAddMode(false)}
                            buttons={[
                              {
                                text: 'Cancel',
                                buttonVariant: 'secondary',
                                onClick: () => setShowAddMode(false),
                              },
                              {
                                text: 'Add',
                                buttonVariant: 'primary',
                                onClick: () => {
                                  const addedMode = { ...initialModes[selectedMode], id: uuidv4() };
                                  arrayHelpers.push(addedMode);
                                  setShowAddMode(false);
                                },
                              },
                            ]}
                          >
                            <div>
                              <SelectInput
                                onChange={(event) => setSelectedMode(event.target.value)}
                                enabled={true}
                                options={addPaymentModeOptions}
                                value={selectedMode}
                                noEmptyValue={true}
                              />
                            </div>
                          </ModalComponent>
                          {values.modes.map((mode, index) => {
                            if (modeComponents[mode.mode]) {
                              const Component = modeComponents[mode.mode];
                              return (
                                <div className="section p-4 mb-3" key={index}>
                                  {(values.modes || defaultModes.modes).length > 1 && (
                                    <span
                                      className="mdi mdi-close delete-icon mt-1"
                                      onClick={() => arrayHelpers.remove(index)}
                                    ></span>
                                  )}
                                  <Component name={`modes.${index}`} mode={mode}></Component>
                                </div>
                              );
                            }

                            return null;
                          })}

                          <Button
                            onClick={() => {
                              setSelectedMode(addPaymentModeOptions[0].value);
                              setShowAddMode(true);
                            }}
                          >
                            {intl.formatMessage({ id: 'paymentModes.addPaymentMode' })}
                          </Button>
                        </div>
                      );
                    }}
                  ></FieldArray>
                </form>
              </React.Fragment>
            );
          }}
        </Formik>
      </div>
    </>
  );
});
PaymentModes.displayName = 'PaymentModes';

export default PaymentModes;
